import React, { useState } from 'react';
import Chart from 'react-apexcharts';
import { Helmet } from 'react-helmet';

const QuarantineFoodCalculator = () => {
  const [people, setPeople] = useState('');
  const [duration, setDuration] = useState('');
  const [mealPref, setMealPref] = useState('');
  const [breakfast, setBreakfast] = useState('');
  const [lunch, setLunch] = useState('');
  const [dinner, setDinner] = useState('');
  const [result, setResult] = useState(null);

  const calculateNeeds = () => {
    if (!people || !duration || !mealPref || !breakfast || !lunch || !dinner) {
      alert('Please fill in all fields!');
      return;
    }

    const peopleNum = parseInt(people);
    const durationNum = parseInt(duration);
    
    // Base calculations
    const mealsPerDay = 3;
    const totalMeals = peopleNum * durationNum * mealsPerDay;
    
    // Calculate quantities based on meal preference
    let foodQuantities = {
      grains: 0,
      proteins: 0,
      vegetables: 0,
      fruits: 0,
      dairy: 0
    };

    switch (mealPref) {
      case 'vegetarian':
        foodQuantities = {
          grains: totalMeals * 0.2,
          proteins: totalMeals * 0.15,
          vegetables: totalMeals * 0.3,
          fruits: totalMeals * 0.2,
          dairy: totalMeals * 0.15
        };
        break;
      case 'non-vegetarian':
        foodQuantities = {
          grains: totalMeals * 0.2,
          proteins: totalMeals * 0.25,
          vegetables: totalMeals * 0.25,
          fruits: totalMeals * 0.2,
          dairy: totalMeals * 0.1
        };
        break;
      case 'vegan':
        foodQuantities = {
          grains: totalMeals * 0.25,
          proteins: totalMeals * 0.2,
          vegetables: totalMeals * 0.3,
          fruits: totalMeals * 0.2,
          dairy: 0
        };
        break;
      case 'gluten-free':
        foodQuantities = {
          grains: totalMeals * 0.15,
          proteins: totalMeals * 0.2,
          vegetables: totalMeals * 0.3,
          fruits: totalMeals * 0.25,
          dairy: totalMeals * 0.1
        };
        break;
      default:
        break;
    }

    // Generate dynamic insights based on inputs
    const insights = [
      {
        title: 'Nutritional Balance',
        value: Math.round((foodQuantities.vegetables + foodQuantities.fruits) / totalMeals * 100),
        description: 'Percentage of meals focused on fruits and vegetables'
      },
      {
        title: 'Protein Source',
        value: mealPref === 'non-vegetarian' ? 'Animal-based' : 'Plant-based',
        description: 'Primary source of protein in your diet'
      },
      {
        title: 'Meal Variety',
        value: new Set([...breakfast.split(','), ...lunch.split(','), ...dinner.split(',')]).size,
        description: 'Number of unique meal items entered'
      },
      {
        title: 'Daily Calories',
        value: Math.round(totalMeals * 500),
        description: 'Estimated daily calorie intake'
      },
      {
        title: 'Water Needs',
        value: Math.round(peopleNum * durationNum * 2),
        description: 'Liters of water needed for the duration'
      },
      {
        title: 'Storage Space',
        value: Math.round((foodQuantities.grains + foodQuantities.proteins + foodQuantities.vegetables + foodQuantities.fruits + foodQuantities.dairy) * 0.05),
        description: 'Estimated storage space needed in cubic meters'
      }
    ];

    // Convert quantities to practical units
    const resultData = {
      totalMeals,
      foodQuantities: {
        grains: (foodQuantities.grains * 0.1).toFixed(1) + ' kg',
        proteins: (foodQuantities.proteins * 0.15).toFixed(1) + ' kg',
        vegetables: (foodQuantities.vegetables * 0.12).toFixed(1) + ' kg',
        fruits: (foodQuantities.fruits * 0.1).toFixed(1) + ' kg',
        dairy: (foodQuantities.dairy * 0.08).toFixed(1) + ' kg'
      },
      dailyBudget: (totalMeals * 150).toFixed(2) + ' INR',
      storageTips: [
        'Store grains in airtight containers',
        'Keep vegetables in the refrigerator',
        'Freeze proteins for longer storage',
        'Keep fruits in a cool, dry place'
      ],
      insights
    };

    setResult(resultData);
  };

  return (
    <>
      <Helmet>
        <title>Quarantine Food Calculator - Plan Your Quarantine Food Needs</title>
        <meta name="description" content="Calculate your food needs during quarantine periods. Get personalized meal plans, budget estimates, and storage recommendations based on your dietary preferences." />
        <meta property="og:title" content="Quarantine Food Calculator" />
        <meta property="og:description" content="Calculate your food needs during quarantine periods. Get personalized meal plans, budget estimates, and storage recommendations based on your dietary preferences." />
        <meta property="og:image" content="https://your-domain.com/quarantine-calculator-preview.png" />
        <meta property="og:url" content="https://your-domain.com/quarantine-calculator" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Quarantine Food Calculator" />
        <meta name="twitter:description" content="Calculate your food needs during quarantine periods. Get personalized meal plans, budget estimates, and storage recommendations based on your dietary preferences." />
        <meta name="twitter:image" content="https://your-domain.com/quarantine-calculator-preview.png" />
      </Helmet>

      <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-white rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-blue-600 mb-2">
            Quarantine Food Calculator
          </h1>
          <p className="text-gray-600">
            Plan your food needs during quarantine periods
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of People
            </label>
            <input
              type="number"
              value={people}
              onChange={(e) => setPeople(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter number of people"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quarantine Duration (days)
            </label>
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter duration in days"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meal Preference
            </label>
            <select 
              value={mealPref}
              onChange={(e) => setMealPref(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select meal preference</option>
              <option value="vegetarian">Vegetarian</option>
              <option value="non-vegetarian">Non-Vegetarian</option>
              <option value="vegan">Vegan</option>
              <option value="gluten-free">Gluten-Free</option>
            </select>
          </div>
        </div>

        <div className="space-y-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Breakfast Items
            </label>
            <input
              type="text"
              value={breakfast}
              onChange={(e) => setBreakfast(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter breakfast items"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lunch Items
            </label>
            <input
              type="text"
              value={lunch}
              onChange={(e) => setLunch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter lunch items"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dinner Items
            </label>
            <input
              type="text"
              value={dinner}
              onChange={(e) => setDinner(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter dinner items"
            />
          </div>
        </div>

        <button
          onClick={calculateNeeds}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-green py-2 px-4 rounded-md hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          Calculate Quarantine Food Needs
        </button>

        {result && (
          <div className="mt-8 p-6 bg-blue-50 rounded-lg">
            <h2 className="text-2xl font-bold text-blue-700 mb-4">Quarantine Food Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center p-4 bg-white rounded-lg shadow">
                <h3 className="text-xl font-semibold text-blue-600">Total Meals</h3>
                <p className="text-2xl font-bold">{result.totalMeals} meals</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow">
                <h3 className="text-xl font-semibold text-blue-600">Daily Budget</h3>
                <p className="text-2xl font-bold">{result.dailyBudget}</p>
              </div>
            </div>

            <div className="mt-4">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Food Quantities</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(result.foodQuantities).map(([food, quantity]) => (
                  <div key={food} className="text-center p-4 bg-white rounded-lg shadow">
                    <h4 className="text-lg font-semibold text-blue-600">{food.charAt(0).toUpperCase() + food.slice(1)}</h4>
                    <p className="text-xl font-bold">{quantity}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Food Distribution</h3>
              <div className="bg-white rounded-lg shadow p-6">
                <Chart
                  options={{
                    chart: {
                      type: 'donut',
                      width: 300,
                    },
                    labels: Object.keys(result.foodQuantities),
                    colors: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#FF5722'],
                    legend: {
                      position: 'bottom',
                      fontSize: '14px',
                    },
                    plotOptions: {
                      pie: {
                        donut: {
                          size: '70%',
                          labels: {
                            show: true,
                            name: {
                              fontSize: '14px',
                            },
                            value: {
                              fontSize: '14px',
                            },
                          },
                        },
                      },
                    },
                    responsive: [
                      {
                        breakpoint: 480,
                        options: {
                          chart: {
                            width: 250,
                          },
                          legend: {
                            position: 'bottom',
                            fontSize: '12px',
                          },
                        },
                      },
                    ],
                  }}
                  series={Object.values(result.foodQuantities).map(q => parseFloat(q))}
                  type="donut"
                />
              </div>
            </div>

            <div className="mt-4">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Insights & Recommendations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {result.insights && result.insights.map((insight, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-lg font-semibold text-blue-600">{insight.title}</h4>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {typeof insight.value === 'number' ? insight.value.toLocaleString() : insight.value}
                      </span>
                    </div>
                    <p className="text-gray-600">{insight.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Storage Tips</h3>
              <div className="space-y-2">
                {result.storageTips.map((tip, index) => (
                  <div key={index} className="bg-white p-3 rounded-lg shadow">
                    <p className="text-gray-700">{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 bg-blue-50 p-4 rounded-lg">
          <h3 className="text-xl font-semibold mb-4">How to Use</h3>
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">1. Enter Basic Details</h4>
              <p>Input the number of people and quarantine duration</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">2. Select Meal Preferences</h4>
              <p>Choose your dietary preference and enter meal items</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">3. Get Results</h4>
              <p>Click calculate to see food quantities and budget</p>
            </div>
          </div>

          <h3 className="text-xl font-semibold mt-4 mb-4">FAQ</h3>
          <div className="space-y-3">
            <div>
              <strong>Q: How accurate are these calculations?</strong>
              <p>These are general guidelines based on average consumption. Adjust according to your specific needs.</p>
            </div>
            <div>
              <strong>Q: Can I modify the quantities?</strong>
              <p>Yes! Use these as a starting point and adjust based on your family's eating habits.</p>
            </div>
          </div>

          <h3 className="text-xl font-semibold mt-4 mb-4">Features & Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">Customizable Meal Plans</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Supports various dietary preferences</li>
                <li>Custom meal item entries</li>
                <li>Flexible duration options</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">Smart Calculations</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Calculates total food requirements</li>
                <li>Breaks down quantities by food group</li>
                <li>Provides budget estimates</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">Storage Tips</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Food preservation advice</li>
                <li>Storage recommendations</li>
                <li>Shelf life information</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">Budget Planning</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Estimated daily budget</li>
                <li>Cost-effective meal suggestions</li>
                <li>Shopping list generation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuarantineFoodCalculator;
